import { useCallback, useContext, useEffect } from 'react';
import ProxyLocalStorage, {
  useProxyLocalStorage,
} from '../v2/ProxyLocalStorage';
import { ConnectType, MetaData, TomoContext } from '..';
import type { EthereumProvider, WalletTgSdk } from '@uxuycom/web3-tg-sdk';

let UXUYProvider: EthereumProvider;

function proxifyUxuyProvider(UXUYConnector: EthereumProvider) {
  const handler: ProxyHandler<EthereumProvider> = {
    get(target: EthereumProvider, property, receiver) {
      switch (property) {
        case 'isConnected': {
          return target.isConnected();
        }
        default:
          return Reflect.get(target, property, receiver);
      }
    },
  };
  return new Proxy(UXUYConnector, handler);
}

async function getUXUYProvider({
  metaData,
  onConnect,
  onDisconnect,
  useEvmChains,
}: {
  metaData: MetaData;
  onConnect?: () => any;
  onDisconnect?: () => any;
  useEvmChains: number[];
}) {
  if (UXUYProvider) return UXUYProvider;
  const UXUYConnector: WalletTgSdk = new (
    await import('@uxuycom/web3-tg-sdk')
  ).WalletTgSdk({
    // injected: true,
    metaData: {
      hostname: metaData.hostname || metaData.name,
      name: metaData.name,
      icon: metaData.icon,
      url: metaData.url || location.origin, // this is a must, to do
    },
  });
  UXUYConnector.ethereum.on('accountsChanged', accounts => {
    console.log('Active account:', accounts, accounts[0]);
    if (accounts?.[0]) {
      onConnect?.();
    } else {
      onDisconnect?.();
    }
  });
  UXUYProvider = proxifyUxuyProvider(UXUYConnector.ethereum);
  return UXUYProvider;
}

interface Option {
  metaData?: MetaData;
  chain?: string;
  theme?: 'dark' | 'light';
}

function useUxuyEVMConnect(option?: Option) {
  const { setProviders, useEvmChains, supportedProviders } = useContext(
    TomoContext
  );

  const supportedProvider = supportedProviders[0];
  const storage = useProxyLocalStorage();

  const connectEffect = (provider: EthereumProvider) => {
    setProviders(pre => ({ ...pre, ethereum: provider }));
    const storage = new ProxyLocalStorage();
    const type: ConnectType = 'UXUY';
    storage.set('connect_type', type);
  };

  const disconnectEffect = () => {
    setProviders(pre => ({ ...pre, ethereum: null }));
    const storage = new ProxyLocalStorage();
    storage.set('connect_type', '');
  };

  const initializeProvider = useCallback(async () => {
    const provider = await getUXUYProvider({
      metaData: option.metaData,
      onConnect: () => {
        connectEffect(provider);
      },
      onDisconnect: () => {
        disconnectEffect();
      },
      useEvmChains,
    });
    return provider;
  }, [option.metaData, useEvmChains]);

  const connected = UXUYProvider?.isConnected
  console.log('connected', connected)

  // reconnection check
  useEffect(() => {
    const connectType = storage.get('connect_type') as ConnectType;
    if (connectType === 'UXUY' && supportedProvider === 'EVM') {
      // try reconnect
      initializeProvider().then(async provider => {
        // restore
        provider.isConnected &&
          setProviders(pre => ({ ...pre, ethereum: provider }));
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supportedProvider, storage]);

  // useEffect(() => {
  //   if (!!UXUYProvider && connected) {
  //     connectEffect(UXUYProvider);
  //   } else if (!!UXUYProvider && !connected) {
  //     disconnectEffect();
  //   }
  // }, [connectEffect, disconnectEffect, connected]);

  const connect = useCallback(async () => {
    const provider = await initializeProvider();
    try {
      const accounts = await provider.request({
        method: 'eth_requestAccounts',
      });
      console.log('result', accounts);
    } catch (error) {
      console.log('error', error);
      throw error;
    }
  }, [initializeProvider]);

  return {
    connect,
  };
}

export default useUxuyEVMConnect;
